/**
 * Chunk-load recovery for the ErrorBoundary (T-14, F-027).
 *
 * After a deploy, a tab that still runs the old build asks for lazy chunks that
 * no longer exist. One reload fetches the new build and fixes it. Any other
 * render error must NOT reload: it would fail again on the next load and loop.
 * The reload budget lives in sessionStorage, so it survives the reload itself.
 */
export const CHUNK_RELOAD_KEY = 'ote_chunk_reload';
/** Automatic reloads allowed inside one window. */
export const MAX_CHUNK_RELOADS = 2;
/** The budget refills after this long without a chunk failure. */
export const CHUNK_RELOAD_WINDOW_MS = 5 * 60 * 1000;

const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i, // Chrome, Edge
  /error loading dynamically imported module/i, // Firefox
  /Importing a module script failed/i, // Safari
  /Loading (CSS )?chunk [\w-]+ failed/i, // webpack-style messages
  /Unable to preload CSS/i, // Vite preload helper
];

export function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const { name, message } = error as { name?: unknown; message?: unknown };
  if (name === 'ChunkLoadError') return true;
  return typeof message === 'string' && CHUNK_ERROR_PATTERNS.some((re) => re.test(message));
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

interface ReloadRecord {
  count: number;
  first: number;
}

function read(storage: StorageLike): ReloadRecord | null {
  try {
    const raw = storage.getItem(CHUNK_RELOAD_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ReloadRecord>;
    if (typeof parsed.count !== 'number' || typeof parsed.first !== 'number') return null;
    return { count: parsed.count, first: parsed.first };
  } catch {
    return null;
  }
}

/**
 * Ask for one automatic reload. Returns true (and spends one unit of the
 * budget) when a reload is allowed; false once the budget for this window is
 * used up, or when storage is unavailable (no way to stop a loop, so no reload).
 */
export function claimChunkReload(storage: StorageLike | null | undefined, now: number): boolean {
  if (!storage) return false;
  const prev = read(storage);
  const fresh = !prev || now - prev.first > CHUNK_RELOAD_WINDOW_MS;
  const record: ReloadRecord = fresh ? { count: 0, first: now } : prev;
  if (record.count >= MAX_CHUNK_RELOADS) return false;
  try {
    storage.setItem(CHUNK_RELOAD_KEY, JSON.stringify({ count: record.count + 1, first: record.first }));
    return true;
  } catch {
    return false;
  }
}

/** sessionStorage, or null where it is blocked. */
export function sessionStorageOrNull(): StorageLike | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}
